import { useTranslation } from 'react-i18next';
import type { SextantKey, PSRScore } from '@/utils/perio';

// ── Types ─────────────────────────────────────────────────────────────────────

interface PsrSextantGridProps {
  scores: Record<SextantKey, PSRScore>;
  /** Highlight these sextant keys (e.g. on click from grid) */
  highlighted?: SextantKey | null;
  onSextantClick?: (key: SextantKey) => void;
}

// ── Score color mapping ────────────────────────────────────────────────────────

function scoreColor(score: PSRScore): string {
  if (score === null) return 'bg-ds-neutral-tint text-ds-muted border-ds-line bg-ds-card border-ds-field';
  if (score === 0)    return 'bg-ds-green-tint text-ds-green-ink border-ds-green-line';
  if (score === 1)    return 'bg-ds-amber-tint text-ds-amber-ink border-ds-amber-line';
  if (score === 2)    return 'bg-ds-amber-tint text-ds-amber-ink border-ds-amber-line';
  if (score === 3)    return 'bg-ds-amber-tint text-ds-amber-ink border-ds-amber-line';
  return              'bg-ds-red-tint text-ds-red-ink border-ds-red'; // score 4
}

// ── Layout: upper row = s1, s2, s3 / lower row = s6, s5, s4 ──────────────────

const SEXTANT_LAYOUT: [SextantKey, SextantKey, SextantKey][] = [
  ['s1', 's2', 's3'],
  ['s6', 's5', 's4'],
];

const SEXTANT_LABELS: Record<SextantKey, string> = {
  s1: 'S1 (17-14)',
  s2: 'S2 (13-23)',
  s3: 'S3 (24-27)',
  s4: 'S4 (47-44)',
  s5: 'S5 (43-33)',
  s6: 'S6 (34-37)',
};

// ── Component ─────────────────────────────────────────────────────────────────

export function PsrSextantGrid({
  scores,
  highlighted,
  onSextantClick,
}: PsrSextantGridProps) {
  const { t } = useTranslation();

  return (
    <div className="rounded-lg border border-ds-line bg-ds-card p-3">
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ds-muted">
        PSR
      </p>
      <div className="space-y-1">
        {SEXTANT_LAYOUT.map((row, rowIdx) => (
          <div key={rowIdx} className="grid grid-cols-3 gap-1">
            {row.map((key) => {
              const score = scores[key];
              const isHighlighted = highlighted === key;

              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => onSextantClick?.(key)}
                  title={SEXTANT_LABELS[key]}
                  className={`
                    flex flex-col items-center justify-center rounded border px-2 py-2 text-center transition-all
                    ${scoreColor(score)}
                    ${isHighlighted ? 'ring-2 ring-ds-teal ring-offset-1' : ''}
                    ${onSextantClick ? 'cursor-pointer hover:opacity-80' : 'cursor-default'}
                  `}
                >
                  <span className="text-xs font-bold">{key.toUpperCase()}</span>
                  <span className="mt-0.5 text-base font-semibold leading-none">
                    {score === null
                      ? t('dental.perio.psr.noData', '–')
                      : t('dental.perio.psr.score', { n: score, defaultValue: String(score) })}
                  </span>
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
